/**
 * Maps technical error codes/patterns to user-friendly messages in PT-BR.
 * Never expose raw error messages to the user.
 */

const ERROR_MAP = {
  // Firebase Auth
  'auth/email-already-in-use': 'Este e-mail já está cadastrado. Tente fazer login.',
  'auth/invalid-email': 'O e-mail informado não é válido.',
  'auth/weak-password': 'A senha deve ter pelo menos 6 caracteres.',
  'auth/user-not-found': 'E-mail ou senha incorretos.',
  'auth/wrong-password': 'E-mail ou senha incorretos.',
  'auth/invalid-credential': 'E-mail ou senha incorretos.',
  'auth/too-many-requests': 'Muitas tentativas. Aguarde alguns minutos e tente novamente.',
  'auth/network-request-failed': 'Erro de conexão. Verifique sua internet.',
  'auth/popup-closed-by-user': 'Login cancelado.',
  'auth/requires-recent-login': 'Por segurança, faça login novamente para continuar.',

  // Cloud Functions
  'functions/internal': 'Ocorreu um erro inesperado. Tente novamente.',
  'functions/unauthenticated': 'Você precisa estar logado para realizar esta ação.',
  'functions/permission-denied': 'Você não tem permissão para realizar esta ação.',
  'functions/not-found': 'O recurso solicitado não foi encontrado.',
  'functions/invalid-argument': 'Dados inválidos. Verifique as informações e tente novamente.',
  'functions/unavailable': 'Serviço temporariamente indisponível. Tente novamente em instantes.',
  'functions/deadline-exceeded': 'A operação demorou demais. Tente novamente.',
  'functions/resource-exhausted': 'Você atingiu o limite do seu plano atual. Faça upgrade para continuar.',

  // Stripe / Pagamento
  'stripe/no-such-price': 'Este plano não está disponível no momento.',
  'stripe/card-declined': 'Cartão recusado. Verifique os dados ou tente outro cartão.',
  'stripe/expired-card': 'Cartão expirado. Use outro cartão.',
  'stripe/incorrect-cvc': 'Código de segurança incorreto.',
  'stripe/processing-error': 'Erro no processamento do pagamento. Tente novamente.',
  'stripe/no-active-subscription': 'Nenhuma assinatura ativa encontrada.',
  'stripe/customer-not-found': 'Dados de pagamento não encontrados. Entre em contato com o suporte.',

  // Assinatura
  'subscription/already-subscribed': 'Você já possui este plano.',
  'subscription/cancel-failed': 'Não foi possível cancelar a assinatura. Tente novamente.',
  'subscription/upgrade-failed': 'Não foi possível atualizar o plano. Tente novamente.',

  // Validação
  'validation/passwords-dont-match': 'As senhas não coincidem.',
  'validation/missing-fields': 'Preencha todos os campos obrigatórios.',
  'validation/invalid-data': 'Verifique as informações e tente novamente.',

  // Rede
  'network/offline': 'Sem conexão com a internet. Verifique sua rede.',
  'network/timeout': 'A conexão expirou. Tente novamente.',
};

const FALLBACK_MESSAGE = 'Ocorreu um erro inesperado. Tente novamente ou entre em contato com o suporte.';

/**
 * Extracts a user-friendly message from any error object.
 * Matches against known patterns — never leaks technical details.
 */
export function getUserFriendlyMessage(error) {
  if (!error) return FALLBACK_MESSAGE;

  // Firebase Auth errors: error.code = 'auth/...'
  if (error.code && ERROR_MAP[error.code]) {
    return ERROR_MAP[error.code];
  }

  // Cloud Functions errors: error.code = 'functions/...'
  // httpsCallable wraps errors as FirebaseError with code like 'functions/internal'
  const functionsCode = `functions/${error.code}`;
  if (ERROR_MAP[functionsCode]) {
    return ERROR_MAP[functionsCode];
  }

  // Check error.message for known Stripe patterns
  const msg = (error.message || error.details || '').toLowerCase();

  if (msg.includes('limite') || msg.includes('limit')) return ERROR_MAP['functions/resource-exhausted'];
  if (msg.includes('no such price')) return ERROR_MAP['stripe/no-such-price'];
  if (msg.includes('card_declined') || msg.includes('card was declined')) return ERROR_MAP['stripe/card-declined'];
  if (msg.includes('expired_card')) return ERROR_MAP['stripe/expired-card'];
  if (msg.includes('incorrect_cvc')) return ERROR_MAP['stripe/incorrect-cvc'];
  if (msg.includes('processing_error')) return ERROR_MAP['stripe/processing-error'];
  if (msg.includes('no active subscription')) return ERROR_MAP['stripe/no-active-subscription'];
  if (msg.includes('no stripe customer')) return ERROR_MAP['stripe/customer-not-found'];
  if (msg.includes('network') || msg.includes('fetch')) return ERROR_MAP['network/offline'];
  if (msg.includes('timeout') || msg.includes('deadline')) return ERROR_MAP['network/timeout'];
  if (msg.includes('permission') || msg.includes('denied')) return ERROR_MAP['functions/permission-denied'];
  if (msg.includes('unauthenticated') || msg.includes('not authenticated')) return ERROR_MAP['functions/unauthenticated'];

  // Fallback — never expose the raw message
  return FALLBACK_MESSAGE;
}

export default ERROR_MAP;
